import { ArrowLeft, type LucideIcon, RefreshCw, RotateCcw, Search } from "lucide-react";
import {
  type AnchorHTMLAttributes,
  type ComponentProps,
  createContext,
  type InputHTMLAttributes,
  type ReactNode,
  useContext,
  useState,
} from "react";
import { Link, type LinkProps, useLocation } from "react-router-dom";
import { reviewContentForDisplay } from "../lib/review-display";
import { reviewTextForDisplay } from "../lib/review-text";
import { cn } from "../lib/utils";
import { useDestinationContext } from "./destination-context";
import { Button, type ButtonVariant, Input } from "./primitives";
import { Button as CyraButton } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Field, FieldError, FieldLabel } from "./ui/field";
import {
  Select as CyraSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const buttonVariant = {
  danger: "destructive",
  ghost: "ghost",
  primary: "default",
  secondary: "outline",
} as const;

export function Disclosure({
  title = "Technical details",
  children,
  open,
}: {
  title?: string;
  children: ReactNode;
  open?: boolean;
}) {
  return (
    <details open={open} className="min-w-0 text-sm">
      <summary className="w-fit cursor-pointer rounded-sm py-2 text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring">
        {title}
      </summary>
      <div className="mt-2 space-y-2 break-words">{children}</div>
    </details>
  );
}

const pageHeaderActiveContext = createContext(true);
const taskDialogContext = createContext(false);

/** Routed records already have one popup Close control. */
export function TaskDialogProvider({ children }: { children: ReactNode }) {
  return <taskDialogContext.Provider value>{children}</taskDialogContext.Provider>;
}

export function useTaskDialog() {
  return useContext(taskDialogContext);
}

export function PageHeaderActivityProvider({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) {
  const parentActive = useContext(pageHeaderActiveContext);
  return (
    <pageHeaderActiveContext.Provider value={parentActive && active}>
      {children}
    </pageHeaderActiveContext.Provider>
  );
}

/** Persistent workspaces keep their drafts, but inactive pages must release portals and blockers. */
export function useWorkspaceActive() {
  return useContext(pageHeaderActiveContext);
}

export function PageHeader({
  actions,
  back,
  center,
  meta,
  title,
}: {
  actions?: ReactNode;
  back?: ReactNode;
  center?: ReactNode;
  meta?: ReactNode;
  title: ReactNode;
}) {
  const active = useContext(pageHeaderActiveContext);
  const inTaskDialog = useTaskDialog();
  if (!active) return null;
  const content = (
    <div
      className={cn(
        "flex flex-wrap items-center justify-end gap-2",
        center && "col-start-2 row-start-1 @[48rem]:col-start-3",
      )}
    >
      {actions}
    </div>
  );
  return (
    <header
      className={cn(
        "@container flex flex-col gap-2",
        inTaskDialog && "sticky top-0 z-10 bg-background pb-2 pr-8",
      )}
      data-ui="page-header"
    >
      {back && !inTaskDialog ? (
        <div className="flex flex-wrap items-center gap-2">{back}</div>
      ) : null}
      <div
        className={
          center
            ? "grid grid-cols-[1fr_auto] items-center gap-3 @[48rem]:grid-cols-[1fr_minmax(12rem,18rem)_1fr]"
            : "flex flex-wrap items-center justify-between gap-2"
        }
      >
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h1
            className="break-words text-lg font-semibold tracking-tight"
            data-focus-subject="true"
          >
            {reviewContentForDisplay(title)}
          </h1>
          {meta ? (
            <span className="text-sm text-muted-foreground">{reviewContentForDisplay(meta)}</span>
          ) : null}
        </div>
        {center ? (
          <div className="col-span-2 row-start-2 min-w-0 justify-self-center @[48rem]:col-span-1 @[48rem]:col-start-2 @[48rem]:row-start-1">
            {center}
          </div>
        ) : null}
        {actions ? content : null}
      </div>
    </header>
  );
}

export function SectionHeader({
  action,
  title,
}: {
  action?: ReactNode;
  description?: ReactNode;
  title: ReactNode;
}) {
  return (
    <header className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
      <h3 className="text-base font-medium">{reviewContentForDisplay(title)}</h3>
      {action}
    </header>
  );
}

interface VerificationCodeInputProps {
  busy?: boolean;
  disabled?: boolean;
  error?: string | undefined;
  id: string;
  label: string;
  onChange: (value: string) => void;
  value: string;
}

export function VerificationCodeInput({
  busy = false,
  disabled = false,
  error,
  id,
  label,
  onChange,
  value,
}: VerificationCodeInputProps) {
  const [focused, setFocused] = useState(false);
  const errorId = `${id}-error`;
  const statusId = `${id}-status`;
  const describedBy = [error ? errorId : "", busy ? statusId : ""].filter(Boolean).join(" ");
  const normalize = (next: string) => next.replace(/\D/gu, "").slice(0, 6);

  return (
    <Field data-invalid={Boolean(error)}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="cyra-otp-input">
        <div className="cyra-otp-group" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div
              className="cyra-otp-slot"
              data-active={focused && index === Math.min(value.length, 5)}
              key={index}
            >
              {value[index] ?? ""}
            </div>
          ))}
        </div>
        <input
          aria-describedby={describedBy || undefined}
          aria-invalid={Boolean(error)}
          aria-label={label}
          autoComplete="one-time-code"
          className="cyra-otp-native"
          disabled={disabled || busy}
          id={id}
          inputMode="numeric"
          maxLength={6}
          onBlur={() => setFocused(false)}
          onChange={(event) => onChange(normalize(event.currentTarget.value))}
          onClick={(event) => event.currentTarget.setSelectionRange(value.length, value.length)}
          onFocus={() => setFocused(true)}
          onPaste={(event) => {
            event.preventDefault();
            onChange(normalize(event.clipboardData.getData("text")));
          }}
          pattern="[0-9]*"
          type="text"
          value={value}
        />
      </div>
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      {busy ? (
        <span className="sr-only" id={statusId} role="status">
          Verifying code…
        </span>
      ) : null}
    </Field>
  );
}

export function FilterBar({ children, label }: { children: ReactNode; label: string }) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: React 19 does not recognize the newer HTML search element yet.
    <div aria-label={label} className="flex min-w-0 flex-wrap items-end gap-2" role="search">
      {children}
    </div>
  );
}

export function CollectionToolbar({
  children,
  count,
  filterLabel,
  label,
}: {
  children?: ReactNode;
  count: number;
  filterLabel: string;
  label: string;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="sr-only">
        <h2 className="sr-only">{label}</h2>
        <span className="text-sm text-muted-foreground">
          {count} {count === 1 ? "record" : "records"}
        </span>
      </div>
      {children ? <FilterBar label={filterLabel}>{children}</FilterBar> : null}
    </section>
  );
}

export function FilterField({
  children,
  compact = false,
  htmlFor,
  label,
}: {
  children: ReactNode;
  compact?: boolean;
  htmlFor: string;
  label: string;
}) {
  return (
    <Field className={cn("min-w-0 flex-1 basis-44 gap-1.5", compact && "basis-28 max-w-56")}>
      <FieldLabel htmlFor={htmlFor}>{label}</FieldLabel>
      {children}
    </Field>
  );
}

interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "type"> {
  id: string;
  label: string;
}

export function SearchField({ id, label, ...props }: SearchFieldProps) {
  return (
    <FilterField htmlFor={id} label={label}>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input {...props} className="pl-9" id={id} type="search" />
      </div>
    </FilterField>
  );
}

interface SelectFieldProps {
  compact?: boolean;
  disabled?: boolean;
  id: string;
  label: string;
  onValueChange: (value: string) => void;
  options: Array<{ label: string; value: string }>;
  value: string;
}

export function SelectField({
  compact = false,
  disabled = false,
  id,
  label,
  onValueChange,
  options,
  value,
}: SelectFieldProps) {
  return (
    <FilterField compact={compact} htmlFor={id} label={label}>
      <CyraSelect onValueChange={onValueChange} value={value}>
        <SelectTrigger
          aria-label={label}
          className="min-w-0 w-full"
          disabled={disabled}
          id={id}
          size="sm"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {reviewTextForDisplay(option.label)}
            </SelectItem>
          ))}
        </SelectContent>
      </CyraSelect>
    </FilterField>
  );
}

export function RefreshAction({
  busy = false,
  label = "Refresh",
  onRefresh,
}: {
  busy?: boolean;
  label?: string;
  onRefresh: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <CyraButton
          aria-label={busy ? "Refreshing records" : label}
          className="size-8 transition-colors"
          disabled={busy}
          onClick={onRefresh}
          size="icon"
          variant="outline"
        >
          <RefreshCw aria-hidden="true" className={busy ? "animate-spin" : undefined} />
          <span className="sr-only">{busy ? "Refreshing records" : label}</span>
        </CyraButton>
      </TooltipTrigger>
      <TooltipContent className="animate-none!">
        {busy ? "Refreshing records" : label}
      </TooltipContent>
    </Tooltip>
  );
}

export function ClearFiltersAction({ onClear }: { onClear: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button aria-label="Clear filters" onClick={onClear} size="small" variant="ghost">
          <RotateCcw aria-hidden="true" />
          <span>Clear filters</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent className="animate-none!">Clear filters</TooltipContent>
    </Tooltip>
  );
}

interface CheckboxFieldProps extends Omit<ComponentProps<typeof Checkbox>, "id"> {
  children: ReactNode;
  id: string;
  trailing?: ReactNode;
}

export function CheckboxField({ children, id, trailing, ...props }: CheckboxFieldProps) {
  return (
    <Field className="rounded-lg border p-3" orientation="horizontal">
      <Checkbox {...props} id={id} />
      <div className="flex flex-1 items-center gap-1">
        <FieldLabel className="cursor-pointer font-normal" htmlFor={id}>
          <span>{reviewContentForDisplay(children)}</span>
        </FieldLabel>
        {trailing}
      </div>
    </Field>
  );
}

interface ExternalLinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
  href: string;
}

export function ExternalLinkButton({ children, className, ...props }: ExternalLinkButtonProps) {
  return (
    <CyraButton asChild className={className}>
      <a {...props}>{children}</a>
    </CyraButton>
  );
}

interface ActionLinkProps extends LinkProps {
  variant?: ButtonVariant;
}

export function ActionLink({
  children,
  className,
  to,
  variant = "ghost",
  ...props
}: ActionLinkProps) {
  const location = useLocation();
  const destination = useDestinationContext();
  return (
    <CyraButton
      asChild
      className={cn(
        "h-auto min-h-8 w-fit max-w-full justify-start whitespace-normal text-left",
        className,
      )}
      data-action-priority={variant === "primary" || variant === "danger" ? "primary" : "secondary"}
      variant={buttonVariant[variant]}
    >
      <Link
        to={typeof to === "string" ? (destination?.href(to) ?? to) : to}
        state={{ returnTo: location.pathname + location.search }}
        {...props}
      >
        {reviewContentForDisplay(children)}
      </Link>
    </CyraButton>
  );
}

export function TextLink({ children, className, ...props }: LinkProps) {
  const location = useLocation();
  const destination = useDestinationContext();
  return (
    <CyraButton
      asChild
      className={cn("h-auto max-w-full whitespace-normal text-left p-0", className)}
      variant="link"
    >
      <Link
        state={{ returnTo: location.pathname + location.search }}
        {...props}
        to={typeof props.to === "string" ? (destination?.href(props.to) ?? props.to) : props.to}
      >
        {reviewContentForDisplay(children)}
      </Link>
    </CyraButton>
  );
}

/** Internal return context survives record reloads without accepting external navigation. */
export function BackLink({ fallback, children }: { fallback: string; children: ReactNode }) {
  const location = useLocation();
  const inTaskDialog = useTaskDialog();
  if (inTaskDialog) return null;
  const candidate: unknown = location.state?.returnTo;
  const to =
    typeof candidate === "string" && /^\/(?!\/)/.test(candidate) && !/[\\\r\n]/.test(candidate)
      ? candidate
      : fallback;
  return (
    <ActionLink
      to={to}
      variant="ghost"
      state={location.state?.returnRowId ? { focusRowId: location.state.returnRowId } : null}
    >
      <ArrowLeft aria-hidden="true" />
      {to.split("?")[0] === fallback.split("?")[0] ? children : "Back to previous view"}
    </ActionLink>
  );
}

export function IconAction({
  controls,
  disabled = false,
  expanded,
  icon: Icon,
  label,
  text,
  onClick,
  type = "button",
  variant = "secondary",
}: {
  controls?: string;
  disabled?: boolean;
  expanded?: boolean;
  icon: LucideIcon;
  label: string;
  text?: string;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: ButtonVariant;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <CyraButton
          aria-controls={controls}
          aria-expanded={expanded}
          aria-label={label}
          className="transition-colors"
          data-action-priority={
            variant === "primary" || variant === "danger" ? "primary" : "secondary"
          }
          disabled={disabled}
          onClick={onClick}
          size="sm"
          type={type}
          variant={buttonVariant[variant]}
        >
          <Icon aria-hidden="true" />
          <span>{text ?? label}</span>
        </CyraButton>
      </TooltipTrigger>
      <TooltipContent className="animate-none!">{label}</TooltipContent>
    </Tooltip>
  );
}

export function IconActionLink({
  icon: Icon,
  label,
  text,
  to,
  variant = "secondary",
}: {
  icon: LucideIcon;
  label: string;
  text?: string;
  to: LinkProps["to"];
  variant?: ButtonVariant;
}) {
  const location = useLocation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <CyraButton
          asChild
          aria-label={label}
          data-action-priority={
            variant === "primary" || variant === "danger" ? "primary" : "secondary"
          }
          size="sm"
          variant={buttonVariant[variant]}
        >
          <Link to={to} state={{ returnTo: location.pathname + location.search }}>
            <Icon aria-hidden="true" />
            <span>{text ?? label}</span>
          </Link>
        </CyraButton>
      </TooltipTrigger>
      <TooltipContent className="animate-none!">{label}</TooltipContent>
    </Tooltip>
  );
}

export function ScreenReaderOnly({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>;
}
